import { Alert, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { AlertCircleIcon } from '@/components/icons/AlertCircleIcon';
import { CheckCircleIcon } from '@/components/icons/CheckCircleIcon';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import { ActionCard } from '@/components/layout/ActionCard/ActionCard';
import { Card } from '@/components/layout/Card/Card';
import { ModalHeader } from '@/components/modals/FullScreenModal/FullScreenFormLayout';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { ProcessingView } from '@/components/progress/ProcessingView/ProcessingView';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import {
  HouseholdChatMessage,
  useHouseholdChatContext,
} from '@/modules/household/chat/contexts/householdChat.context';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { romie, ROMIE_BOLD_WEIGHT } from '@/styles/fonts/romie';
import { COLORS } from '@/styles/tokens/colors';
import { TITLE_SERIF_SIZING } from '@/styles/tokens/fonts';
import { MessageStatus } from '@/types/schema';

import { ValidationQuestion } from './ValidationQuestion';
import {
  getValidationQuestions,
  getValidationResponse,
} from './validationQuestions.utils';

export function SetupValidationQuestions({
  householdId,
  householdName,
  message,
  state = 'preparing',
  onAnswersAccepted,
}: {
  householdId: string;
  householdName: string;
  message?: HouseholdChatMessage;
  state?: 'completed' | 'preparing' | 'unavailable';
  onAnswersAccepted?: () => void;
}) {
  const trowser = useTrowser(TrowserKey.ValidationQuestions);
  const canEdit = useCanEditHouseholdData();
  const trackUserEvent = useTrackUserEvent();
  const [acceptedSubmissionId, setAcceptedSubmissionId] = useState<string>();
  // The host stays mounted when the modal closes, preserving unfinished drafts.
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>>>(
    {}
  );
  const submission = useRef<
    | {
        id: string;
        isGrouped: boolean;
        method: 'submitted' | 'skip_all';
        questionCount: number;
        answeredCount: number;
      }
    | undefined
  >(undefined);
  const { respondToMessage, respondingToMessage, messages, error } =
    useHouseholdChatContext();
  const { close } = trowser;
  useEffect(() => {
    const pendingSubmission = submission.current;
    const hasAcceptedSubmission =
      pendingSubmission &&
      acceptedSubmissionId === pendingSubmission.id &&
      messages.some(
        (item) =>
          item.id === pendingSubmission.id &&
          item.status === MessageStatus.Responded
      );
    if (hasAcceptedSubmission) {
      // close() flushes navigation state synchronously, so run after this effect.
      const timer = setTimeout(() => {
        // One grouped response completes the question set. A legacy single
        // question can have more questions after it, so retain timestamp gating.
        if (pendingSubmission.isGrouped) {
          trackUserEvent('ai_onboarding validation_questions_completed', {
            householdId,
            messageId: pendingSubmission.id,
            method: pendingSubmission.method,
            questionCount: pendingSubmission.questionCount,
            answeredCount: pendingSubmission.answeredCount,
            blankCount:
              pendingSubmission.questionCount - pendingSubmission.answeredCount,
          });
          onAnswersAccepted?.();
        }
        submission.current = undefined;
        if (trowser.isOpen && trowser.target === householdId) close();
      }, 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [
    acceptedSubmissionId,
    messages,
    close,
    trowser.isOpen,
    trowser.target,
    householdId,
    onAnswersAccepted,
    trackUserEvent,
  ]);
  if (!trowser.isOpen || trowser.target !== householdId || !canEdit)
    return null;

  const questions = getValidationQuestions(message);
  const answers = message ? (drafts[message.id] ?? {}) : {};
  const questionAnswers = questions.flatMap(({ id }) => {
    const answer = answers[id]?.trim();
    return answer ? [{ id, answer }] : [];
  });
  const submitting = !!message && respondingToMessage?.messageId === message.id;
  const submit = (skipAll = false) => {
    if (!message || message.status !== MessageStatus.Pending || submitting)
      return;
    setAcceptedSubmissionId(undefined);
    submission.current = {
      id: message.id,
      isGrouped: !!message.groupedQuestionContent,
      method: skipAll ? 'skip_all' : 'submitted',
      questionCount: questions.length,
      answeredCount: skipAll ? 0 : questionAnswers.length,
    };
    void respondToMessage(
      getValidationResponse(message, skipAll ? {} : answers)
    ).then((succeeded) => {
      if (succeeded) {
        setAcceptedSubmissionId(message.id);
      } else {
        submission.current = undefined;
      }
    });
    // Require mutation success and durable RESPONDED before tracking or closing.
  };

  return (
    <FullScreenModal
      isOpen
      trowserKey={TrowserKey.ValidationQuestions}
      onClose={close}
    >
      <Stack
        sx={{
          minHeight: '100%',
          bgcolor: COLORS.PRIMITIVES.WHITE,
        }}
      >
        <ModalHeader
          heading="Validation questions"
          rightContent={
            <IconButton
              ariaLabel="Close validation questions"
              icon={XCloseIcon}
              variant="transparent"
              size="md"
              onClick={close}
            />
          }
        />
        <Stack
          spacing={1.5}
          useFlexGap
          sx={{
            width: '100%',
            maxWidth: 804,
            alignSelf: 'center',
            px: 2,
            py: { xs: 3, md: 6 },
          }}
        >
          <Typography
            variant="h1_display"
            component="h1"
            sx={{
              fontFamily: romie.style.fontFamily,
              fontWeight: ROMIE_BOLD_WEIGHT,
              ...TITLE_SERIF_SIZING.l,
            }}
          >
            {householdName
              ? `Validation questions for the ${householdName} household`
              : 'Validation questions'}
          </Typography>
          {message ? (
            <>
              <Typography variant="body-m">
                These resolve facts the documents don’t settle. Answer what you
                know and leave the rest blank.
              </Typography>
              {error && (
                <Alert severity="error">
                  We couldn’t load or update your questions. Your draft is saved
                  here. Please try again shortly.
                </Alert>
              )}
              <Card
                variant="outlined"
                sx={{
                  width: '100%',
                  maxWidth: 700,
                  alignSelf: 'center',
                  mt: 3,
                  p: 0,
                  overflow: 'hidden',
                }}
              >
                <Stack spacing={3} sx={{ px: 2, py: 1.5 }}>
                  {questions.map((question, index) => (
                    <ValidationQuestion
                      key={question.id}
                      question={question}
                      number={index + 1}
                      value={answers[question.id] ?? ''}
                      disabled={submitting}
                      onChange={(value) =>
                        setDrafts((current) => ({
                          ...current,
                          [message.id]: {
                            ...current[message.id],
                            [question.id]: value,
                          },
                        }))
                      }
                    />
                  ))}
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ sm: 'center' }}
                  sx={{
                    borderTop: `1px solid ${COLORS.GRAY[200]}`,
                    mx: 2,
                    py: 1.5,
                  }}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={close}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Cancel
                  </Button>
                  <Stack direction="row" spacing={1.5}>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={submitting}
                      onClick={() => submit(true)}
                    >
                      Skip all
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={submitting || !questionAnswers.length}
                      onClick={() => submit()}
                    >
                      {submitting ? 'Submitting…' : 'Submit'}
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            </>
          ) : (
            <Stack
              role="status"
              sx={{ py: { xs: 4, md: 8 }, alignItems: 'center' }}
            >
              <ValidationQuestionsStatus
                state={state}
                error={error}
                onClose={close}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    </FullScreenModal>
  );
}

function ValidationQuestionsStatus({
  state,
  error,
  onClose,
}: {
  state: 'completed' | 'preparing' | 'unavailable';
  error: string | null;
  onClose: () => void;
}) {
  // A completed question set stays complete even if a later chat request fails.
  if (state === 'completed') {
    return (
      <ActionCard
        variant="noCard"
        icon={<CheckCircleIcon size={32} color={COLORS.TEXT.PRIMARY} />}
        heading="Validation questions completed"
        description="These validation questions have already been completed for this household. There’s nothing else you need to do here."
        actions={
          <Button variant="primary" size="md" onClick={onClose}>
            Back to overview
          </Button>
        }
        sx={{ maxWidth: 400 }}
      />
    );
  }
  if (error || state === 'unavailable') {
    return (
      <ActionCard
        variant="noCard"
        icon={<AlertCircleIcon size={32} color={COLORS.TEXT.PRIMARY} />}
        heading="Validation questions unavailable"
        description="We can’t show validation questions right now. Please return to the overview and try again later."
        actions={
          <Button variant="secondary" size="md" onClick={onClose}>
            Back to overview
          </Button>
        }
        sx={{ maxWidth: 400 }}
      />
    );
  }
  return (
    <ProcessingView
      title="Preparing your questions"
      subtitle="We’re checking the household’s documents for details that need clarification. Your questions will appear here when they’re ready."
    />
  );
}
